import {
    createRouter,
    createRoute,
    createRootRoute,
    redirect,
    Outlet,
} from '@tanstack/react-router'

import {
    AppShell,
    IntentRoute,
    DashboardRoute,
    DashboardDetailRoute,
    MapTabRoute,
    PlatformAdminRoute,
} from './App'

import AcceptOrgInvitePage from './pages/AcceptOrgInvitePage'
import AcceptInvitePage from './pages/AcceptInvitePage'
import ProjectDetailPage from './pages/ProjectDetailPage'
import WellFinderReviewPage from './pages/WellFinderReviewPage'

import FieldPage from './pages/FieldPage'
import RoutePage from './pages/RoutePage'
import SitePage from './pages/SitePage'
import SessionUnavailablePage from './pages/SessionUnavailablePage'
import WorkspaceShell from './workspace/WorkspaceShell'
import PortalShellSelect, { PORTAL_HOME } from './portal/PortalShellSelect'
import PortalPlanReviewsPage from './portal/PortalPlanReviewsPage'
import PortalLauncherPage from './portal/PortalLauncherPage'
import PortalReviewPage from './portal/PortalReviewPage'
import PadPlannerDashboardPage from './padplanner/DashboardPage'
import PadPlannerPlansListPage from './padplanner/PlansListPage'
import PadPlannerNewPlanPage from './padplanner/NewPlanPage'
import PadPlannerPlanPage from './padplanner/PlanPage'
import PadPlannerEditorRoute from './padplanner/EditorRoute'
import PadPlannerLibraryPage from './padplanner/LibraryPage'
import WorkspaceLandingPage from './workspace/WorkspaceLandingPage'
import WorkspaceReferencePage from './workspace/WorkspaceReferencePage'
import FeatureCellPage from './workspace/FeatureCellPage'
import RecheckPage from './workspace/RecheckPage'

import { useNavigate } from '@tanstack/react-router'

function AcceptOrgInviteWrapper() {
    const { inviteId } = acceptOrgInviteRoute.useParams()
    const navigate = useNavigate()
    return <AcceptOrgInvitePage inviteId={inviteId} navigate={navigate} />
}

function AcceptInviteWrapper() {
    const { inviteId } = acceptInviteRoute.useParams()
    const navigate = useNavigate()
    return <AcceptInvitePage inviteId={inviteId} navigate={navigate} />
}

function PortalReviewWrapper() {
    const { planReviewId } = portalReviewRoute.useParams()
    return <PortalReviewPage planReviewId={planReviewId} />
}

function PadPlannerPlanWrapper() {
    const { planId } = padplannerPlanRoute.useParams()
    return <PadPlannerPlanPage planId={planId} />
}

function ProjectDetailWrapper() {
    const { projectId } = projectDetailRoute.useParams()
    const navigate = useNavigate()
    return <ProjectDetailPage projectId={projectId} navigate={navigate} />
}

import { useAuthStore } from './stores/useAuthStore'

// ── Auth guard ─────────────────────────────────────────────────────────────
const requireAuth = async () => {
    // KC is initialized before ReactDOM.createRoot in main.jsx,
    // so window.__keycloak is always set by render time.
    // If for any reason it isn't, fail open rather than redirect-looping.
    const kc = window.__keycloak
    if (!kc) return  // KC not ready — let it through, KC will redirect if needed
    if (!kc.authenticated) {
        // Preserve the destination (incl. ?caseId deep-links) through the KC
        // round-trip — a bare kc.login() drops the user at '/' after auth.
        // The target URL must be registered in the KC client's valid redirect
        // URIs (PF-4). window.location.href is current as of beforeLoad.
        kc.login({ redirectUri: window.location.href })
        return
    }
}

// The home route each confined account belongs to, and the paths it may reach.
// One table rather than a chain of ifs: adding a third confined role is a row,
// and a row cannot forget to handle the invite flows below.
const HOMES = {
    '/map/padrekko': {
        // PadRekko demo operators. Existing behaviour, unchanged.
        reaches: (p) => p === '/map/padrekko',
        to: { to: '/map/$tab', params: { tab: 'padrekko' } },
    },
    '/workspace': {
        // WellFinder analysts.
        reaches: (p) => p === '/workspace' || p.startsWith('/workspace/'),
        to: { to: '/workspace' },
    },
    // COMPUTED KEY, so the portal's home is spelled ONCE on the client. The
    // shell branch needs the same value to tell a confined submitter from an
    // entitled reviewer, and two literals that must match is the shape that
    // produces a drift nobody sees. router.authMeContract.test.jsx pins this
    // table to homeRouteFor; PortalShellSelect.test.jsx pins the constant.
    [PORTAL_HOME]: {
        // Operator Portal submitters — third parties inside a customer's org.
        //
        // SUBTREE-SHAPED LIKE /workspace, NOT EXACT-MATCH LIKE /map/padrekko,
        // and the difference is easy to miss because the two rows above already
        // differ in exactly this way. /portal/$planReviewId is a child route,
        // so an exact-match `reaches` would bounce a submitter off their own
        // plan review and back to the index, every time, in a loop.
        // SUBTREE, AND DELIBERATELY NOT INCLUDING '/portal' ITSELF.
        //
        // Still subtree-shaped, so the list and every plan review under it stay
        // reachable -- an exact-match predicate here would bounce a submitter
        // off their own plan review and back, forever, which is why the
        // /map/padrekko row above and this one differ in exactly this way.
        //
        // What changed is the bare '/portal'. That is the launcher, and a
        // submitter has one surface, so being sent from it to their home IS
        // "submitters never see the index" -- expressed in the table that
        // already decides where confined accounts belong, rather than in a
        // second redirect somewhere else.
        reaches: (p) => p.startsWith('/portal/'),
        to: { to: PORTAL_HOME },
    },
}

// Invite links are reachable by anyone confined, because a demo user or an
// analyst may arrive on one from email before they have ever seen their home.
const ALWAYS_REACHABLE = (p) =>
    p.startsWith('/accept-org-invite/') || p.startsWith('/accept-invite/')

// Day 9 Day 2 pinned PadRekko operators to /map/padrekko with a boolean. This
// is that mechanism generalised, per ACCESS_BOUNDARY §6 — the SAME decision
// point, not a parallel one, because two half-boundaries is the failure that
// document names.
//
// The home route is computed by the SERVER (routes/auth.mjs:homeRouteFor) from
// is_restricted_operator and the same role predicate the restricted-account
// gate uses. The client never re-derives it: a client that disagrees with the
// server routes an analyst into a shell where every call 403s, which renders as
// a blank page and reads as a broken app rather than a permissions boundary.
const requireAuthAndAllowedRoute = async ({ location }) => {
    await requireAuth()

    const store = useAuthStore.getState()
    if (!store.profile && !store.profileLoading && store.token) {
        await store.loadProfile()
    }

    const state = useAuthStore.getState()

    // PROFILE LOOKUP FAILED. profileLoaded is set on success or failure, so
    // "settled with nothing" is the signal. Neither default is acceptable
    // here: fail-open drops an analyst into a platform where every call 403s
    // (the blank screen), and fail-closed to a guessed home sends someone
    // somewhere the server never confirmed. Terminal retry state instead — it
    // cannot loop, because /session-unavailable does not consult the profile.
    if (state.token && state.profileLoaded && !state.profile) {
        const path = location?.pathname ?? ''
        if (path === '/session-unavailable') return
        throw redirect({ to: '/session-unavailable', replace: true })
    }

    // Not settled yet. loadProfile is in flight on a cold start, and treating
    // "no profile yet" as "no home_route" would bounce every user to the retry
    // state before the first fetch returns. Wait; the route re-runs when the
    // store updates.
    if (!state.profile) return

    // ABSENT FIELD IS NOT THE SAME AS null.
    //
    //   home_route: null      staff — the server looked and has no opinion
    //   no home_route key     the server never looked. An API older than this
    //                         UI, or a response shape we do not recognise.
    //
    // Collapsing those with `?? null` is what produced the reported failure:
    // an analyst signed in, /auth/me came back WITHOUT home_route because the
    // running admin-api predates it, the client read undefined as "staff", and
    // he landed on the Command Center. Silently, with nothing to grep for.
    //
    // Unclassifiable takes the same terminal retry state as a failed lookup,
    // for the same reason: guessing "staff" is the guess that put an analyst on
    // a platform where every call 403s. It also makes UI/API skew loud at the
    // first sign-in rather than silent until someone notices where they landed.
    if (!('home_route' in state.profile)) {
        const p = location?.pathname ?? ''
        if (p === '/session-unavailable') return
        console.error(
            '[router] /auth/me returned no home_route. The API is older than this UI; ' +
            'routing cannot be determined and would otherwise silently default to the platform.',
        )
        throw redirect({ to: '/session-unavailable', replace: true })
    }

    const home = state.profile.home_route

    // NULL IS STAFF. The server looked and has no opinion, which is the answer
    // for anyone who is not confined. Unchanged, and the arm most likely to be
    // collateral in an edit to the next one.
    if (!home) return

    // A HOME THE CLIENT DOES NOT KNOW IS NOT STAFF, AND USED TO BE TREATED AS
    // STAFF.
    //
    // This read `if (!home || !HOMES[home]) return`, collapsing two different
    // states into the permissive one. The server is FAIL-CLOSED on a role class
    // it cannot place -- restrictedAccountGate resolves an unreadable role set
    // to the NARROWEST class -- and the client was FAIL-OPEN on a home it could
    // not place, which is the same disagreement in the opposite direction.
    //
    // It was live: homeRouteFor has returned '/portal' since the confinement
    // class landed, and until this row existed that value hit this line and
    // rendered the PLATFORM shell for an account confined to /api/v1/portal/.
    // Every call on that shell 403s, so the symptom is a blank page that reads
    // as a broken app rather than as a boundary -- the exact failure
    // ACCESS_BOUNDARY §6 exists to remove.
    //
    // Same terminal state as the other two unclassifiable cases above, for the
    // same reason: guessing "staff" is the guess that put an analyst on the
    // Command Center.
    //
    // ── WHY IT CANNOT LOOP, AND IT IS NOT THE LINE BELOW ────────────────────
    //
    // sessionUnavailableRoute carries `beforeLoad: requireAuth` -- NOT
    // requireAuthAndAllowedRoute -- so this function never runs there and the
    // redirect can never re-fire. That is the mechanism, and
    // router.homeRoute.test.jsx pins it structurally: swap that route's guard
    // and the test fails, because the swap is what would make the loop
    // possible.
    //
    // The pathname check below is therefore UNREACHABLE TODAY, as are the two
    // identical ones in the arms above it. Kept as the backstop for exactly
    // that swap, and labelled rather than left to read as the thing doing the
    // work -- measured 2026-09-17: with it removed, arriving at
    // /session-unavailable still logs nothing and still does not redirect,
    // because this function is not called.
    if (!HOMES[home]) {
        const p = location?.pathname ?? ''
        if (p === '/session-unavailable') return   // backstop; unreachable while that route uses requireAuth
        console.error(
            `[router] /auth/me returned home_route "${home}", which this UI has no route for. ` +
            'The UI is older than the API, or a confined role class shipped without its client half. ' +
            'Routing cannot be determined and would otherwise silently default to the platform.',
        )
        throw redirect({ to: '/session-unavailable', replace: true })
    }

    const path = location?.pathname ?? ''
    if (HOMES[home].reaches(path) || ALWAYS_REACHABLE(path)) return

    throw redirect({ ...HOMES[home].to, replace: true })
}

// ── Root ──────────────────────────────────────────────────────────────────
//
// THE ROOT RENDERS NO SHELL. It used to render AppShell, which meant every
// route in the application got the platform chrome — including
// /wellfinder-review, which is why an access-boundary recon of that page
// observed PadRekko QC, AOI, project and job calls it has no use for.
//
// Shell selection is now STRUCTURAL: two pathless layout routes sit directly
// under the root, and which one matches decides which shell mounts.
//
//   _platform   -> AppShell        everything that existed before
//   _workspace  -> WorkspaceShell  /workspace/*
//   _portal     -> PortalShellSelect  /portal/*  -- see below, it is TWO
//
// Deliberately not a conditional inside AppShell. Its platform hooks
// (useOperatingAoiInit, useRestrictedCaseSelection, useRestrictedInitialLayers,
// useImpersonationFromUrl, loadProjects…) run before any conditional return
// could fire, so a flag would still make every one of those requests. The
// analyst would keep collecting 403s and the workspace would be a stripped
// platform rather than its own surface.
export const rootRoute = createRootRoute({
    component: () => <Outlet />,
})

// ── Layout: platform ──────────────────────────────────────────────────────
// Pathless (`id`, no `path`), so it adds a shell without adding a URL segment.
// Every pre-existing route hangs off this and none of their paths change.
export const platformLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: '_platform',
    component: AppShell,
})

// ── Layout: workspace ─────────────────────────────────────────────────────
export const workspaceLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: '_workspace',
    component: WorkspaceShell,
})

// ── Layout: portal ────────────────────────────────────────────────────────
// Operator Portal submitters — third parties inside a customer's org — and the
// reviewers who answer them. Which of the two a caller is comes from the
// SERVER, on route 6's `viewer_role`.
//
// THE TWO DO NOT GET THE SAME SHELL, AND THE ROUTER CANNOT BE THE ONE TO
// CHOOSE. A confined submitter gets PortalShell; an entitled reviewer gets the
// app shell, because they are an ordinary member of their org and the portal is
// one product among the ones they hold. Both arrive at the same `/portal`, so
// there is no second path to hang a second layout route on — layout routes are
// matched by path and the predicate is not a path.
//
// PortalShellSelect is therefore a chooser, not a shell: it reads the server's
// `home_route` and mounts one of the two. That is NOT the `if (isPortal)`
// inside AppShell this file's header forbids — the conditional sits ABOVE the
// mount, and React runs no hooks of a component it does not render. See
// PortalShellSelect.jsx for the argument and PortalShellSelect.test.jsx for the
// hook count that holds it.
export const portalLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: '_portal',
    component: PortalShellSelect,
})

// ── / ─────────────────────────────────────────────────────────────────────
export const intentRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/',
    beforeLoad: requireAuthAndAllowedRoute,
    component: IntentRoute,
})

// ── /dashboard ────────────────────────────────────────────────────────────
export const dashboardRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/dashboard',
    beforeLoad: requireAuthAndAllowedRoute,
    component: DashboardRoute,
})

export const dashboardDetailRoute = createRoute({
    getParentRoute: () => dashboardRoute,
    path: '$dashId',
    component: DashboardDetailRoute,
})

// ── /map — layout shell only, no redirect logic ───────────────────────────
// This route exists purely as a parent container. It renders <Outlet /> so
// the matched child ($tab) renders into it. No beforeLoad redirect here —
// that was causing the infinite loop (mapRoute redirects → mapTabRoute loads
// → mapRoute fires again → loop).
export const mapRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/map',
    // requireAuthAndAllowedRoute, NOT requireAuth. This route renders AppShell
    // through its Outlet, so typing the bare /map put an analyst on the platform
    // shell with every call 403ing — a blank page. The $tab child was guarded
    // and the parent was not, and navigating anywhere afterwards redirected
    // correctly, which is what made it invisible.
    //
    // The pattern to watch for: A PARENT PATH THAT RENDERS WITHOUT MATCHING A
    // GUARDED CHILD. Covered by router.barePaths.test.jsx, which derives every
    // typeable path and every parent prefix from the tree itself.
    beforeLoad: requireAuthAndAllowedRoute,
    component: () => <Outlet />,
})

// ── /map/$tab ─────────────────────────────────────────────────────────────
// Day 9 Day 2 — the route gate enforces restricted users land on
// /map/padrekko only. Other tabs redirect via requireAuthAndAllowedRoute.
export const mapTabRoute = createRoute({
    getParentRoute: () => mapRoute,
    path: '$tab',
    beforeLoad: requireAuthAndAllowedRoute,
    component: MapTabRoute,
    validateSearch: (search) => ({
        zoom: search.zoom != null ? Number(search.zoom) : undefined,
        lat: search.lat != null ? Number(search.lat) : undefined,
        lng: search.lng != null ? Number(search.lng) : undefined,
        mapMode: typeof search.mapMode === 'string' ? search.mapMode : undefined,
        // Phase 4 — case deep-link. /map/padrekko?caseId=<id> auto-loads the
        // case in ReclamationQcTab, which strips the param once loaded
        // (one-shot entry; see ReclamationQcTab for the rationale).
        caseId: typeof search.caseId === 'string' ? search.caseId : undefined,
        // PadRekko case-builder deep-link. "Create New Case" opens
        // /map/explore?mapMode=padrekko_builder&returnTo=padrekko; returnTo is
        // the product tab Continue/Cancel routes back to.
        returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
        // PadRekko builder hand-off. "Continue to case details" opens
        // /map/padrekko?newcase=selection so PadRekkoDashboard mounts NewCaseForm
        // in from-selection mode (pads come from useCaseBuilderStore).
        newcase: typeof search.newcase === 'string' ? search.newcase : undefined,
    }),
})

// ── /platform-admin ───────────────────────────────────────────────────────
// ── /field — AI PRIME FieldAssist ─────────────────────────────────────────
//
// The mobile shell, and the parent of every field product. Shell-less with
// respect to AppShell, off rootRoute, which renders a bare <Outlet />.
//
// Registration follows sessionUnavailableRoute — rootRoute parent, a component,
// a beforeLoad guard — but uses requireAuthAndAllowedRoute rather than that
// route's bare requireAuth. sessionUnavailableRoute needs the bare one because
// it is the redirect TARGET of a profile failure and consulting the profile
// there would loop. /field is not that target, so the fuller guard is safe and
// keeps this page on the same confinement rules as everything else.
//
// WHAT IT DOES NOT DO: add a HOMES entry. No account resolves to a FieldAssist
// home today, because no field-rep account type exists — not a Keycloak realm
// role, not an aiprime.roles row, not a licence tier, not a capability, and
// is_restricted_operator is false for every account on this database. A HOMES
// row now would route nobody. It becomes a one-row addition, reaching the
// /field SUBTREE rather than /route specifically, once the account type exists.
// A LAYOUT, not a page: it renders <Outlet /> so the matched child renders into
// it, exactly as mapRoute does for $tab. The shell itself is the index child
// below. A parent that rendered FieldPage directly would have nowhere to put
// /field/route and the child would never appear.
export const fieldRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/field',
    beforeLoad: requireAuthAndAllowedRoute,
    component: () => <Outlet />,
})

export const fieldIndexRoute = createRoute({
    getParentRoute: () => fieldRoute,
    path: '/',
    component: FieldPage,
})

// ── /field/route — road routing to a pad ──────────────────────────────────
//
// A CHILD OF /field, not a sibling. It was briefly a top-level shell-less route
// and before that a child of platformLayoutRoute; parenting it here is what
// makes the third HOMES entry, when it comes, a statement about FieldAssist
// rather than about one page inside it.
//
// It keeps its own full-viewport layout — /field renders <Outlet /> and nothing
// around it, so the child owns the screen exactly as it did standing alone.
//
// The guard is inherited from the parent and NOT repeated. Restating
// requireAuthAndAllowedRoute here would run the profile check twice per
// navigation and, worse, leave two places to keep in step.
export const routeRoute = createRoute({
    getParentRoute: () => fieldRoute,
    path: 'route',
    component: RoutePage,
})

// ── /field/site — SiteAgent, what is on a pad ──────────────────────────────
//
// A SECOND CHILD OF fieldRoute, registered exactly as routeRoute is. That is
// the whole point of the shell FieldPage describes: "the second field product
// is a second card, which is a registration rather than a rework." This is the
// registration.
//
// Same shell-less full-viewport shape — fieldRoute renders a bare <Outlet />,
// so SitePage owns the screen the way RoutePage does. The guard is inherited
// from the parent and NOT repeated: restating requireAuthAndAllowedRoute here
// would run the profile check twice per navigation and leave two places to keep
// in step.
export const siteRoute = createRoute({
    getParentRoute: () => fieldRoute,
    path: 'site',
    component: SitePage,
})

export const platformAdminRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/platform-admin',
    beforeLoad: requireAuthAndAllowedRoute,
    component: PlatformAdminRoute,
})

export const acceptOrgInviteRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/accept-org-invite/$inviteId',
    beforeLoad: requireAuth,
    component: AcceptOrgInviteWrapper,
})

export const acceptInviteRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/accept-invite/$inviteId',
    beforeLoad: requireAuth,
    component: AcceptInviteWrapper,
})

// ── /wellfinder-review ────────────────────────────────────────────────────
// Adjudication review. A dedicated top-level route, not a /map tab: 3c drives
// single-record review from the keyboard and those bindings would compete with
// free map exploration.
//
// Guarded by requireAuth only. The DreamHammer-org restriction is enforced by
// the SERVER (requireAdjudicationAccess, 403 code 'not_adjudication_org'); the page
// checks useEffectiveOrg purely so a non-DH user gets an explanation instead of
// a screen of 403s. A beforeLoad redirect would have to read org state that is
// null during impersonation load, and failing closed there means bouncing a
// legitimate user mid-switch.
//
// tier / sort / status / aoi live in the URL so the first real session — the
// 200-record A' calibration, which is driven by a tier filter rather than the
// default sort — is bookmarkable and reproducible.
export const workspaceReviewRoute = createRoute({
    getParentRoute: () => workspaceLayoutRoute,
    path: '/workspace/review',
    // Guarded like every other shell-rendering route: an operator typing
    // /workspace must be bounced to /map/padrekko, not shown the analyst shell.
    // An analyst is unaffected — reaches() returns true for /workspace/*.
    beforeLoad: requireAuthAndAllowedRoute,
    component: WellFinderReviewPage,
    validateSearch: (search) => ({
        // Repeatable in the API; a comma-joined string in the URL, normalised
        // to an array by the page. Undefined means "no filter", which is not
        // the same as an empty array.
        tier: typeof search.tier === 'string' && search.tier ? search.tier : undefined,
        status: typeof search.status === 'string' && search.status ? search.status : undefined,
        sort: search.sort === 'fastest' ? 'fastest'
            : search.sort === 'thorough' ? 'thorough'
            : undefined,
        // The open record (3b). A record is a linkable thing — "look at this
        // one" is the most common message an analyst sends about this queue.
        // Validated as a uuid rather than passed through: the page fetches
        // /records/:id with it, and the server 400s a non-uuid, so an
        // unvalidated param turns a typo into an error screen.
        record: typeof search.record === 'string'
            && /^[0-9a-f-]{36}$/i.test(search.record) ? search.record : undefined,
        // The operating AOI, for the same reason tier and sort are here: the
        // URL is this page's source of truth for filters, so a param this
        // function does not return DOES NOT EXIST as far as the page is
        // concerned.
        //
        // THAT IS HOW THE AOI PICKER SHIPPED INERT. The store, the query-string
        // builder and the server all handled it, and the picker wrote it — this
        // whitelist dropped it one layer above them, so the page's filter effect
        // never saw a change and never called setFilters. No error, no console
        // warning; the picker simply did nothing.
        //
        // uuid-shaped like `record` above and for the same reason: the page
        // sends it to an endpoint that 400s a non-uuid, so validating here turns
        // a hand-edited URL into "no filter" rather than an error screen.
        aoi: typeof search.aoi === 'string'
            && /^[0-9a-f-]{36}$/i.test(search.aoi) ? search.aoi : undefined,
    }),
})

export const projectDetailRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/projects/$projectId',
    beforeLoad: requireAuthAndAllowedRoute,
    component: ProjectDetailWrapper,
})

// ── /padplanner ───────────────────────────────────────────────────────────
// PadPlanner. Under the platform layout because it is one product among the
// ones an org holds, reached from the TopNav strip. Guarded like every
// shell-rendering route, so a confined account is sent home. The PADPLANNER
// entitlement is each page's to show, not this guard's: see
// padplanner/PadPlannerGate.jsx.
//
// S2 adds the list, the new-plan form and the plan page; S4 the editor at
// /padplanner/plans/$planId/edit. Siblings, not children: each is a page of
// its own, and the platform layout renders them. /review (S6) does not exist yet.
export const padplannerRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/padplanner',
    beforeLoad: requireAuthAndAllowedRoute,
    component: PadPlannerDashboardPage,
})

// ?status= is a list tab: one of the five statuses, or "archived". Anything
// else is "all", so a hand-typed value cannot reach the server as a filter.
const PADPLANNER_LIST_TABS = ['draft', 'in_review', 'changes_requested', 'approved', 'issued', 'archived']
export const padplannerPlansRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/padplanner/plans',
    beforeLoad: requireAuthAndAllowedRoute,
    validateSearch: (search) => ({
        status: PADPLANNER_LIST_TABS.includes(search.status) ? search.status : undefined,
    }),
    component: PadPlannerPlansListPage,
})

export const padplannerNewRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/padplanner/new',
    beforeLoad: requireAuthAndAllowedRoute,
    component: PadPlannerNewPlanPage,
})

// ?tab= opens a panel tab: the editor's "Add wells" link lands on Wells.
const PADPLANNER_PLAN_TABS = ['review', 'layout', 'details', 'wells', 'templates']
export const padplannerPlanRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/padplanner/plans/$planId',
    beforeLoad: requireAuthAndAllowedRoute,
    validateSearch: (search) => ({
        tab: PADPLANNER_PLAN_TABS.includes(search.tab) ? search.tab : undefined,
    }),
    component: PadPlannerPlanWrapper,
})

// S4: the editor. Entered from the plan page when GET /plans/:id says
// can_edit; a read-only viewer who types the URL is told so (EditorPage.jsx).
export const padplannerEditorRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/padplanner/plans/$planId/edit',
    beforeLoad: requireAuthAndAllowedRoute,
    component: PadPlannerEditorRoute,
})

// S3: the template library. Every PadPlanner user reads it; the writes are
// the API's to refuse (library admins only).
export const padplannerLibraryRoute = createRoute({
    getParentRoute: () => platformLayoutRoute,
    path: '/padplanner/library',
    beforeLoad: requireAuthAndAllowedRoute,
    component: PadPlannerLibraryPage,
})



// ── /workspace ────────────────────────────────────────────────────────────
// Landing. Placeholder in step 1 — the content in §4 of the design is step 2.
export const workspaceLandingRoute = createRoute({
    getParentRoute: () => workspaceLayoutRoute,
    path: '/workspace',
    // Guarded like every other shell-rendering route: an operator typing
    // /workspace must be bounced to /map/padrekko, not shown the analyst shell.
    // An analyst is unaffected — reaches() returns true for /workspace/*.
    beforeLoad: requireAuthAndAllowedRoute,
    component: WorkspaceLandingPage,
})

// ── /workspace/reference ──────────────────────────────────────────────────
export const workspaceReferenceRoute = createRoute({
    getParentRoute: () => workspaceLayoutRoute,
    path: '/workspace/reference',
    // Guarded like every other shell-rendering route: an operator typing
    // /workspace must be bounced to /map/padrekko, not shown the analyst shell.
    // An analyst is unaffected — reaches() returns true for /workspace/*.
    beforeLoad: requireAuthAndAllowedRoute,
    component: WorkspaceReferencePage,
})

// ── THREE ROUTES WHERE THERE WERE TWO ─────────────────────────────────────
//
//   /portal                              the launcher — a card per surface
//   /portal/plan-reviews                 the list
//   /portal/plan-reviews/$planReviewId   one plan review
//
// THE DETAIL NESTS UNDER THE LIST rather than staying at /portal/$id. With the
// list at a static /portal/plan-reviews and the detail at a dynamic
// /portal/$planReviewId, both patterns match the same URL and only TanStack's
// static-before-dynamic ordering decides it. Ids are uuids so nothing collides
// in practice, but a tree whose correctness rests on a matcher's tie-break is a
// tree that breaks when someone adds /portal/settings. Nesting removes the
// question rather than answering it.

// ── /portal ───────────────────────────────────────────────────────────────
//
// A CONFINED SUBMITTER NEVER GETS HERE, and no code in this route says so.
// HOMES[PORTAL_HOME].reaches admits '/portal/...' and not '/portal' itself, so
// requireAuthAndAllowedRoute sends them to their home -- which IS the list. The
// existing mechanism, with nothing new bolted to it.
export const portalIndexRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: '/portal',
    beforeLoad: requireAuthAndAllowedRoute,
    component: PortalLauncherPage,
})

// ── /portal/plan-reviews ──────────────────────────────────────────────────
export const portalPlanReviewsRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: '/portal/plan-reviews',
    beforeLoad: requireAuthAndAllowedRoute,
    component: PortalPlanReviewsPage,
})

// ── /portal/plan-reviews/$planReviewId ────────────────────────────────────
export const portalReviewRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: '/portal/plan-reviews/$planReviewId',
    beforeLoad: requireAuthAndAllowedRoute,
    component: PortalReviewWrapper,
})

// ── /workspace/features ───────────────────────────────────────────────────
//
// The Feature Adjudicator cell list. Sibling of /workspace/review rather than a
// route of its own tree: both are adjudication surfaces under the same shell,
// and reaches() already admits every /workspace/* path, so an analyst confined
// to the workspace gets here without widening the boundary.
//
// NO validateSearch. The list takes no parameters -- it is 15 rows in one fixed
// order. The detail view will want a cell in the URL ("look at this one" is the
// message people send), and that is the point at which this gains a search
// schema, validated the way workspaceReviewRoute validates `record`.
//
// ACCESS IS SERVER-SIDE, as with /workspace/review: the routes gate on
// feature_reviewer and the org context, and the landing page decides whether to
// show the card. A beforeLoad role check here would be a second, client-side
// copy of a rule the server already owns -- and the one that renders blank when
// the two disagree.
export const workspaceFeaturesRoute = createRoute({
    getParentRoute: () => workspaceLayoutRoute,
    path: '/workspace/features',
    beforeLoad: requireAuthAndAllowedRoute,
    // The SAME component as the $cellX/$cellY route: one page, and the path
    // segment only decides whether a cell starts selected. A separate list
    // component would be a second owner of the same state.
    component: FeatureCellPage,
})

// ── /workspace/features/$cellX/$cellY ─────────────────────────────────────
//
// One cell's deferrals. The cell is IN THE PATH rather than in search params
// because it identifies the resource -- "look at this cell" is the message
// people send, and a path segment is what survives being pasted into chat.
//
// Params are validated by the PAGE, not here. cellX and cellY are signed
// integers (the live corpus runs -7157..-7002 on x), and a non-integer must
// render "no such cell" rather than throwing inside a route matcher where the
// error surfaces as a blank shell.
//
// No search schema yet. Which GROUP a reviewer is on is session state, not a
// linkable thing -- the groups are derived client-side and their ordering is a
// property of this fetch, so a ?group=4 would name something that could move.
export const workspaceFeatureCellRoute = createRoute({
    getParentRoute: () => workspaceLayoutRoute,
    path: '/workspace/features/$cellX/$cellY',
    beforeLoad: requireAuthAndAllowedRoute,
    component: FeatureCellPage,
})

// ── /workspace/recheck and /workspace/recheck/$cellX/$cellY ───────────────
//
// Re-checking earlier wells on a new collect (build item 9, step 4). The same
// shape as /workspace/features: one page, the cell in the path, access decided
// by the server (routes/feature-recheck.mjs). Its landing card (workspace/tools.js)
// is shown to the callers that gate admits: the DreamHammer org, either reviewer role.
export const workspaceRecheckRoute = createRoute({
    getParentRoute: () => workspaceLayoutRoute,
    path: '/workspace/recheck',
    beforeLoad: requireAuthAndAllowedRoute,
    // The operating AOI the page filters by, validated as /workspace/review
    // validates it (router.recheckSearch.test.jsx).
    validateSearch: (search) => ({
        aoi: typeof search.aoi === 'string'
            && /^[0-9a-f-]{36}$/i.test(search.aoi) ? search.aoi : undefined,
    }),
    component: RecheckPage,
})

export const workspaceRecheckCellRoute = createRoute({
    getParentRoute: () => workspaceLayoutRoute,
    path: '/workspace/recheck/$cellX/$cellY',
    beforeLoad: requireAuthAndAllowedRoute,
    // The operating AOI the page filters by, validated as /workspace/review
    // validates it (router.recheckSearch.test.jsx).
    validateSearch: (search) => ({
        aoi: typeof search.aoi === 'string'
            && /^[0-9a-f-]{36}$/i.test(search.aoi) ? search.aoi : undefined,
    }),
    component: RecheckPage,
})

// ── /wellfinder-review → /workspace/review ────────────────────────────────
//
// KEPT AS A REDIRECT, NOT DELETED. The training deck's URLs point at this path
// and analysts have been given it directly; a 404 on a path someone was told to
// use reads as a broken application rather than as a moved page.
//
// Search params are carried through, because the ones that matter are exactly
// the ones people share: ?record=<id> is "look at this one", and ?tier=c is the
// A' calibration entry point.
//
// REMOVE once the training deck is updated. It costs one route and it will
// outlive its purpose silently, which is how a compatibility shim becomes
// permanent.
export const wellfinderReviewLegacyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/wellfinder-review',
    beforeLoad: ({ search }) => {
        throw redirect({ to: '/workspace/review', search, replace: true })
    },
})

// ── /session-unavailable ──────────────────────────────────────────────────
// Terminal state when the profile cannot be read. Shell-less, and guarded by
// requireAuth ONLY — it must never consult the profile, or the redirect that
// sends people here would send them here again.
export const sessionUnavailableRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/session-unavailable',
    beforeLoad: requireAuth,
    component: SessionUnavailablePage,
})

// ── Tree ──────────────────────────────────────────────────────────────────
export const routeTree = rootRoute.addChildren([
    platformLayoutRoute.addChildren([
        intentRoute,
        dashboardRoute.addChildren([dashboardDetailRoute]),
        mapRoute.addChildren([mapTabRoute]),
        platformAdminRoute,
        acceptOrgInviteRoute,
        acceptInviteRoute,
        projectDetailRoute,
        padplannerRoute,
        padplannerPlansRoute,
        padplannerNewRoute,
        padplannerPlanRoute,
        padplannerEditorRoute,
        padplannerLibraryRoute,
    ]),
    workspaceLayoutRoute.addChildren([
        workspaceLandingRoute,
        workspaceReviewRoute,
        workspaceReferenceRoute,
        workspaceFeaturesRoute,
        workspaceFeatureCellRoute,
        workspaceRecheckRoute,
        workspaceRecheckCellRoute,
    ]),
    portalLayoutRoute.addChildren([
        portalIndexRoute,
        portalPlanReviewsRoute,
        portalReviewRoute,
    ]),
    // Shell-less: it only ever redirects, so mounting it under a layout would
    // render a shell for the instant before the throw.
    wellfinderReviewLegacyRoute,
    sessionUnavailableRoute,
    // Shell-less for a different reason: these are phone views, and AppShell's
    // rail and header are neither usable nor dismissable at 390px.
    fieldRoute.addChildren([fieldIndexRoute, routeRoute, siteRoute]),
])

export const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultStaleTime: 5_000,
})